/* =================================================================
   ROCO 4×4 - theme.js
   Vanilla JS chrome: cart drawer (Shopify AJAX API), YMM bar,
   mega menus, mobile menu, predictive search, theme toggle, toasts.
   ================================================================= */

(function() {
  "use strict";

  /* ----------- utilities ----------- */
  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => Array.from(root.querySelectorAll(s));
  const on = (el, ev, fn, opts) => { if (el) el.addEventListener(ev, fn, opts); };
  const fmt = (cents, currency) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || (window.Shopify?.currency?.active || "USD") }).format(cents / 100);
    } catch {
      return "$" + (cents / 100).toFixed(2);
    }
  };
  const debounce = (fn, ms = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ----------- toast ----------- */
  let toastTimer;
  function showToast(msg) {
    const t = $("#roco-toast");
    if (!t) return;
    $(".toast__msg", t).textContent = msg;
    t.classList.add("toast--show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove("toast--show"), 2600);
  }

  /* ============================================================
     THEME TOGGLE (light / dark)
     ============================================================ */
  function initThemeToggle() {
    const stored = localStorage.getItem("roco_theme");
    if (stored) document.documentElement.setAttribute("data-theme", stored);

    $$('[data-theme-toggle]').forEach((btn) => {
      on(btn, "click", () => {
        const cur = document.documentElement.getAttribute("data-theme") || "light";
        const next = cur === "dark" ? "light" : "dark";
        document.documentElement.setAttribute("data-theme", next);
        localStorage.setItem("roco_theme", next);
        document.dispatchEvent(new CustomEvent("theme:changed", { detail: { theme: next } }));
      });
    });
  }

  /* ============================================================
     MEGA MENU (desktop)
     ============================================================ */
  function initMegaMenu() {
    const items = $$('[data-mega-trigger]');
    let closeTimer;

    items.forEach((trigger) => {
      const key = trigger.dataset.megaTrigger;
      const panel = $(`[data-mega-panel="${key}"]`);
      if (!panel) return;

      const open = () => {
        clearTimeout(closeTimer);
        items.forEach((t) => {
          const k = t.dataset.megaTrigger;
          const p = $(`[data-mega-panel="${k}"]`);
          if (p) p.classList.toggle("megamenu--open", k === key);
          t.setAttribute("aria-expanded", k === key ? "true" : "false");
        });
      };
      const closeWithDelay = () => {
        closeTimer = setTimeout(() => {
          panel.classList.remove("megamenu--open");
          trigger.setAttribute("aria-expanded", "false");
        }, 120);
      };

      on(trigger, "mouseenter", open);
      on(trigger, "focus", open);
      on(trigger, "mouseleave", closeWithDelay);
      on(panel, "mouseenter", () => clearTimeout(closeTimer));
      on(panel, "mouseleave", closeWithDelay);

      on(trigger, "keydown", (e) => {
        if (e.key === "Escape") {
          panel.classList.remove("megamenu--open");
          trigger.setAttribute("aria-expanded", "false");
          trigger.blur();
        }
      });
    });
  }

  /* ============================================================
     MOBILE MENU
     ============================================================ */
  function initMobileMenu() {
    const menu = $("#mobile-menu");
    const openers = $$('[data-mobile-menu-open]');
    const closers = $$('[data-mobile-menu-close]');
    if (!menu) return;

    const open = () => {
      menu.classList.add("mobile-menu--open");
      document.body.style.overflow = "hidden";
      menu.setAttribute("aria-hidden", "false");
    };
    const close = () => {
      menu.classList.remove("mobile-menu--open");
      document.body.style.overflow = "";
      menu.setAttribute("aria-hidden", "true");
    };

    openers.forEach((b) => on(b, "click", open));
    closers.forEach((b) => on(b, "click", close));

    // Expand / collapse submenus
    $$('[data-mobile-submenu-toggle]', menu).forEach((btn) => {
      on(btn, "click", () => {
        const target = btn.nextElementSibling;
        if (!target) return;
        const isOpen = target.classList.toggle("mobile-menu__sub--open");
        btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
    });
  }

  /* ============================================================
     CART DRAWER + AJAX CART
     ============================================================ */
  const Cart = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = $("#cart-drawer");
      this.overlay = $("#cart-overlay");
      if (!this.drawer) return;

      // Open / close
      $$('[data-cart-open]').forEach((b) => on(b, "click", (e) => { e.preventDefault(); this.open(); }));
      $$('[data-cart-close]').forEach((b) => on(b, "click", () => this.close()));
      on(this.overlay, "click", () => this.close());
      on(document, "keydown", (e) => { if (e.key === "Escape") this.close(); });

      // Bind add-to-cart forms (delegated)
      on(document, "submit", (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        e.preventDefault();
        this.add(form);
      });

      // Bind quick-add buttons
      on(document, "click", (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.addById(btn.dataset.quickAdd, parseInt(btn.dataset.quickAddQty || 1, 10), btn);
      });

      // Bind "Buy It Now" - add then go straight to checkout (all payment methods)
      on(document, "click", (e) => {
        const btn = e.target.closest('[data-buy-now]');
        if (!btn) return;
        e.preventDefault();
        const form = btn.closest("form");
        const variantId = form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value;
        const qtyEl = form && form.querySelector('input[name="quantity"]');
        const quantity = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
        if (!variantId) return;
        this.buyNow(variantId, quantity, btn);
      });

      // Bind drawer line item quantity changes
      on(this.drawer, "click", (e) => {
        const inc = e.target.closest('[data-cart-inc]');
        const dec = e.target.closest('[data-cart-dec]');
        const rm = e.target.closest('[data-cart-remove]');
        if (inc) {
          const newQty = parseInt(inc.dataset.qty, 10) + 1;
          const max = inc.dataset.max ? parseInt(inc.dataset.max, 10) : Infinity;
          this.change(inc.dataset.key, Math.min(newQty, max));
        } else if (dec) this.change(dec.dataset.key, parseInt(dec.dataset.qty, 10) - 1);
        else if (rm) { e.preventDefault(); this.change(rm.dataset.key, 0); }
      });
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add("drawer--open");
      this.overlay?.classList.add("drawer-overlay--open");
      this.drawer.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove("drawer--open");
      this.overlay?.classList.remove("drawer-overlay--open");
      this.drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    },

    async add(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      const formData = new FormData(form);
      try {
        const res = await fetch(window.routes.cart_add_url + ".js", {
          method: "POST",
          headers: { "Accept": "application/javascript", "X-Requested-With": "XMLHttpRequest" },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || "Add to cart failed");
        showToast("Added to cart");
        await this.refresh();
        this.open();
      } catch (err) {
        console.error("Cart add error:", err);
        showToast(err.message || "Could not add to cart");
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    },

    async addById(variantId, quantity, btn) {
      if (btn) btn.disabled = true;
      try {
        const res = await fetch(window.routes.cart_add_url + ".js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: variantId, quantity }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || "Add to cart failed");
        showToast("Added to cart");
        await this.refresh();
        this.open();
      } catch (err) {
        console.error(err);
        showToast(err.message || "Could not add to cart");
      } finally {
        if (btn) btn.disabled = false;
      }
    },

    async buyNow(variantId, quantity, btn) {
      const original = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "Redirecting…"; }
      try {
        const res = await fetch(window.routes.cart_add_url + ".js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: variantId, quantity }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || "Add to cart failed");
        window.location.href = window.routes.root_url ? window.routes.root_url.replace(/\/$/, "") + "/checkout" : "/checkout";
      } catch (err) {
        console.error("Buy now error:", err);
        showToast(err.message || "Could not start checkout");
        if (btn) { btn.disabled = false; btn.textContent = original; }
      }
    },

    async change(key, quantity) {
      try {
        const res = await fetch(window.routes.cart_change_url + ".js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error("Cart update failed");
        await this.refresh();
      } catch (err) { console.error(err); }
    },

    async refresh() {
      try {
        const res = await fetch("/?sections=cart-drawer,header-cart-count");
        if (!res.ok) return;
        const sections = await res.json();
        if (sections["cart-drawer"]) {
          const tmp = document.createElement("div");
          tmp.innerHTML = sections["cart-drawer"];
          const fresh = tmp.querySelector("#cart-drawer");
          if (fresh && this.drawer) this.drawer.innerHTML = fresh.innerHTML;
        }
        if (sections["header-cart-count"]) {
          const countEls = $$('[data-cart-count]');
          const tmp = document.createElement("div");
          tmp.innerHTML = sections["header-cart-count"];
          const newCount = tmp.querySelector('[data-cart-count]');
          if (newCount) countEls.forEach((el) => { el.textContent = newCount.textContent; el.classList.toggle("is-zero", newCount.textContent.trim() === "0"); });
        }
      } catch (err) { console.error("Cart refresh:", err); }
    },
  };

  /* ============================================================
     YMM BAR (Year / Make / Model)
     Persists to localStorage. On select, sets a cookie for Liquid-side
     filtering and redirects to fitment page or filtered collection.
     ============================================================ */
  /* The live store tags fitment as "Make-Model[-submodel]-Year"
     (e.g. "Chevrolet-Silverado-1500-2020"). The vehicle's make/model/year
     come straight from the taxonomy in those same tag tokens, so the tag is
     just a hyphen join - no slugify (which would clobber the model hyphens). */
  // Mirror Shopify's handleize so the tag URL + soft-fitment matching are
  // robust to spaces / parens in make/model (e.g. "Land Rover", "Bronco-(2-Door)").
  const handleize = (s) => String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  // Year is optional: with a year we filter on the Make-Model-Year tag, without
  // a year on the broader Make-Model tag (both exist in the catalog).
  const vehTag = (v) => handleize(v.year ? `${v.make}-${v.model}-${v.year}` : `${v.make}-${v.model}`);
  const vehLabel = (v) => ((v.year ? v.year + " " : "") + v.make + " " + String(v.model).replace(/-/g, " ")).trim();

  /* Soft fitment: leave the URL alone and badge the product cards that fit
     the saved vehicle. Cards expose their fitment tags via [data-fitment-tags]. */
  const SoftFitment = {
    init() {
      let v = null;
      try { v = JSON.parse(localStorage.getItem("roco_vehicle") || "null"); } catch {}
      if (!v || !v.make || !v.model || !v.year) return;
      const tag = vehTag(v);
      const cards = document.querySelectorAll("[data-fitment-tags]");
      cards.forEach((card) => {
        const tags = (card.getAttribute("data-fitment-tags") || "").split(" ").map(handleize);
        if (tags.indexOf(tag) === -1) return;
        if (card.querySelector(".badge--fits")) return;
        const slot = card.querySelector(".product-card__badges");
        if (!slot) return;
        const b = document.createElement("span");
        b.className = "badge badge--fits";
        b.textContent = "✓ Fits";
        slot.appendChild(b);
        card.classList.add("product-card--fits");
      });
    },
  };

  const YMM = {
    init() {
      const root = $("[data-ymm]");
      if (!root) return;

      this.root = root;
      this.data = window.RocoVehicleData || {};

      this.makeSel = $('[data-ymm-make]', root);
      this.modelSel = $('[data-ymm-model]', root);
      this.yearSel = $('[data-ymm-year]', root);
      this.submitBtn = $('[data-ymm-submit]', root);
      this.clearBtns = $$('[data-ymm-clear]', root);

      this.restore();
      this.bind();
    },

    saved() {
      try { return JSON.parse(localStorage.getItem("roco_vehicle") || "null"); } catch { return null; }
    },

    save(v) {
      if (v) {
        localStorage.setItem("roco_vehicle", JSON.stringify(v));
        document.cookie = `roco_vehicle=${encodeURIComponent(JSON.stringify(v))}; path=/; max-age=31536000`;
      } else {
        localStorage.removeItem("roco_vehicle");
        document.cookie = "roco_vehicle=; path=/; max-age=0";
      }
      document.dispatchEvent(new CustomEvent("ymm:change", { detail: v }));
    },

    restore() {
      const v = this.saved();
      if (!v) return;
      // The DOM ships both rows; CSS swaps which is visible based on
      // data-ymm-state. Populate the readout in the selected row.
      this.root.dataset.ymmState = "selected";
      const cur = this.root.querySelector("[data-ymm-current]");
      if (cur) cur.textContent = vehLabel(v);
      const shop = this.root.querySelector("[data-ymm-shop]");
      if (shop) shop.href = this.targetUrl(v);
    },

    // Where "Find Parts" / "Shop Parts For This Vehicle" sends the customer.
    // Default: filter the catalog by the real fitment tag. Override: a
    // configured results page receiving ?make=&model=&year=.
    targetUrl(v) {
      const configured = this.root.dataset.ymmRedirect;
      if (configured && configured.trim() && configured.trim() !== "/pages/fitment") {
        return configured.trim() + `?make=${encodeURIComponent(v.make)}&model=${encodeURIComponent(v.model)}&year=${encodeURIComponent(v.year || "")}`;
      }
      return `/collections/all/${vehTag(v)}`;
    },

    bind() {
      if (!this.makeSel) return;

      // Populate the Make select from the loaded taxonomy. The inline
      // pre-paint script in ymm-bar.liquid can't depend on the deferred
      // roco-vehicle-data.js asset (it runs first, before the data exists),
      // so Make is filled here - theme.js boots on DOMContentLoaded, by which
      // point the deferred data asset has executed and this.data is populated.
      // Guard against double-fill when the data was inlined (metaobject branch).
      if (this.makeSel.options.length <= 1) {
        Object.keys(this.data).sort().forEach((make) => {
          const o = document.createElement("option");
          o.value = make; o.textContent = make;
          this.makeSel.appendChild(o);
        });
      }

      const populateModels = (make) => {
        this.modelSel.innerHTML = '<option value="">Select Model</option>';
        const models = this.data[make] ? Object.keys(this.data[make]).sort() : [];
        models.forEach((m) => {
          const o = document.createElement("option");
          o.value = m; o.textContent = String(m).replace(/-/g, " ");
          this.modelSel.appendChild(o);
        });
        this.modelSel.disabled = models.length === 0;
        this.yearSel.disabled = true;
        this.yearSel.innerHTML = '<option value="">Select Year</option>';
        this.checkValid();
      };

      const populateYears = (make, model) => {
        this.yearSel.innerHTML = '<option value="">Select Year</option>';
        const years = (this.data[make] && this.data[make][model]) || [];
        years.forEach((y) => {
          const o = document.createElement("option");
          o.value = y; o.textContent = y;
          this.yearSel.appendChild(o);
        });
        this.yearSel.disabled = years.length === 0;
        this.checkValid();
      };

      on(this.makeSel, "change", () => populateModels(this.makeSel.value));
      on(this.modelSel, "change", () => populateYears(this.makeSel.value, this.modelSel.value));
      on(this.yearSel, "change", () => this.checkValid());

      on(this.submitBtn, "click", (e) => {
        e.preventDefault();
        if (!this.makeSel.value || !this.modelSel.value) return; // year optional
        const v = { make: this.makeSel.value, model: this.modelSel.value, year: this.yearSel.value || "" };
        this.save(v);
        // Filter the catalog by the real fitment tag
        // (/collections/all/<make>-<model>-<year>), or a configured results page.
        window.location.href = this.targetUrl(v);
      });

      this.clearBtns.forEach((btn) => on(btn, "click", (e) => {
        e.preventDefault();
        this.save(null);
        window.location.reload();
      }));
    },

    checkValid() {
      const ok = this.makeSel.value && this.modelSel.value; // year optional
      if (this.submitBtn) { this.submitBtn.disabled = !ok; this.submitBtn.style.opacity = ok ? 1 : 0.5; }
    },
  };

  /* ============================================================
     PREDICTIVE SEARCH
     Uses Shopify's built-in /search/suggest endpoint.
     ============================================================ */
  const PredictiveSearch = {
    init() {
      const form = $("[data-search-form]");
      if (!form) return;
      const input = $('input[name="q"]', form);
      const results = $("[data-search-results]");
      if (!input || !results) return;

      const search = debounce(async (q) => {
        if (q.length < 2) {
          results.innerHTML = "";
          results.classList.remove("search-results--open");
          return;
        }
        try {
          const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url);
          if (!res.ok) return;
          const html = await res.text();
          // Shopify returns the rendered section HTML wrapped in the parent
          // section markup; pull the inner result wrapper.
          const tmp = document.createElement("div");
          tmp.innerHTML = html;
          const inner = tmp.querySelector("[data-predictive-results]") || tmp;
          results.innerHTML = inner.innerHTML;
          results.classList.add("search-results--open");
        } catch (err) { console.error("Predictive search:", err); }
      }, 200);

      on(input, "input", (e) => search(e.target.value.trim()));
      on(input, "focus", () => { if (input.value.length >= 2) results.classList.add("search-results--open"); });
      on(document, "click", (e) => { if (!form.contains(e.target)) results.classList.remove("search-results--open"); });
    },
  };

  /* ============================================================
     PRODUCT QUANTITY STEPPER
     ============================================================ */
  function initQuantitySteppers() {
    on(document, "click", (e) => {
      const inc = e.target.closest('[data-qty-inc]');
      const dec = e.target.closest('[data-qty-dec]');
      if (!inc && !dec) return;
      const wrap = (inc || dec).closest('[data-qty-wrap]');
      if (!wrap) return;
      const input = wrap.querySelector("input");
      if (!input) return;
      let v = parseInt(input.value, 10) || 1;
      if (inc) {
        const max = input.max ? parseInt(input.max, 10) : Infinity;
        v = Math.min(v + 1, max);
      } else {
        v = Math.max(1, v - 1);
      }
      input.value = v;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  /* ============================================================
     PDP — variant picker + image swap
     ============================================================ */
  function initVariantPicker() {
    const pickers = $$('[data-variant-picker]');
    pickers.forEach((picker) => {
      const product = JSON.parse($('[data-product-json]', picker.closest('[data-product-root]'))?.textContent || "null");
      if (!product) return;
      const inputs = $$('input[name^="option"]', picker);
      const idInput = $('[name="id"]', picker.closest("form") || document);

      const update = () => {
        const selected = [];
        ["option1", "option2", "option3"].forEach((name) => {
          const sel = picker.querySelector(`input[name="${name}"]:checked`);
          if (sel) selected.push(sel.value);
        });
        const variant = product.variants.find((v) => {
          return selected.every((val, idx) => v.options[idx] === val);
        });
        if (!variant) return;
        if (idInput) idInput.value = variant.id;
        const evt = new CustomEvent("variant:change", { detail: variant });
        document.dispatchEvent(evt);

        // Update price display
        const priceEl = $('[data-product-price]');
        const compareEl = $('[data-product-compare]');
        if (priceEl) priceEl.textContent = fmt(variant.price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (compareEl) {
          if (onSale) {
            compareEl.textContent = fmt(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        // Save badge (percentage off) + Shop Pay installment teaser
        const saveEl = $('[data-product-save]');
        if (saveEl) {
          if (onSale) {
            saveEl.textContent = "SAVE " + Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) + "%";
            saveEl.hidden = false;
          } else {
            saveEl.hidden = true;
          }
        }
        const instEl = $('[data-pay-installment]');
        if (instEl) instEl.textContent = fmt(Math.round(variant.price / 4));

        // Keep the shipping estimator's unit price in sync with the chosen variant
        const shipEst = $('[data-ship-est]');
        if (shipEst) {
          shipEst.dataset.unitPrice = variant.price;
          document.dispatchEvent(new CustomEvent("shipest:price", { detail: variant.price }));
        }

        // Update availability. When sold out, hide the in-stock line entirely -
        // the "Notify Me When Back" button below carries that message (no dup).
        const stockEl = $('[data-product-availability]');
        if (stockEl) {
          stockEl.textContent = variant.available ? "● In Stock" : "";
          stockEl.dataset.available = variant.available ? "1" : "0";
          const line = stockEl.closest('[data-availability-line]');
          if (line) line.hidden = !variant.available;
        }

        const atc = $('[data-add-to-cart-btn]');
        if (atc) {
          atc.disabled = !variant.available;
          atc.textContent = variant.available ? atc.dataset.labelAvailable || "Add to Cart" : atc.dataset.labelUnavailable || "Sold Out";
        }

        // Hide "Buy It Now" on a sold-out variant (instant checkout makes no sense).
        const buyNow = $('[data-buy-now]');
        if (buyNow) buyNow.hidden = !variant.available;

        // Notify the back-in-stock widget so it shows/hides + keeps its hidden
        // variant_id/sku in sync with the selected variant.
        document.dispatchEvent(new CustomEvent("roco:variant-availability", { detail: { available: !!variant.available, variant: variant } }));

        // Clamp quantity input to the selected variant's available stock
        const qtyInput = $('input[name="quantity"]', picker.closest('[data-product-root]') || document);
        if (qtyInput) {
          if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
            qtyInput.max = variant.inventory_quantity;
            const cur = parseInt(qtyInput.value, 10) || 1;
            if (cur > variant.inventory_quantity) qtyInput.value = Math.max(1, variant.inventory_quantity);
          } else {
            qtyInput.removeAttribute('max');
          }
        }

        // Swap gallery to the variant's featured image. Prefer syncing through
        // the gallery controller (keeps thumb/index/counter in sync); fall back
        // to a direct swap if the variant image isn't one of the thumbs.
        if (variant.featured_media) {
          const fsrc = variant.featured_media.preview_image.src;
          const synced = window.__pdpGallerySelectSrc && window.__pdpGallerySelectSrc(fsrc);
          if (!synced) {
            const main = $('[data-product-main-image]');
            if (main) {
              // srcset overrides src for display, so update both or it won't change.
              main.src = fsrc;
              main.srcset = fsrc;
            }
          }
        }
      };
      inputs.forEach((i) => on(i, "change", update));
    });

    // Tabs
    $$('[data-pdp-tab]').forEach((tab) => {
      on(tab, "click", () => {
        const key = tab.dataset.pdpTab;
        $$('[data-pdp-tab]').forEach((t) => t.classList.toggle("pdp__tab--active", t.dataset.pdpTab === key));
        $$('[data-pdp-tab-content]').forEach((c) => { c.hidden = c.dataset.pdpTabContent !== key; });
      });
    });

    // ---- Gallery: thumbs + nav arrows + keyboard + zoom modal ----
    // Shows every product image (not a hard-capped 6), scrolls the thumb rail,
    // swaps the main image instantly by preloading neighbours, and supports
    // prev/next arrows + keyboard ArrowLeft/ArrowRight (in page + in the modal).
    (function initGallery() {
      const stage = $('[data-main-stage]');
      const mainImg = $('[data-product-main-image]');
      const thumbsWrap = $('[data-thumbs]');
      const thumbs = $$('[data-thumb]');
      if (!mainImg || thumbs.length === 0) return;

      const countEl = $('[data-gallery-count]');
      const zoomModal = $('[data-image-modal]');
      const zoomImg = $('[data-image-modal-img]');
      let modalOpen = false;

      let idx = thumbs.findIndex((t) => t.classList.contains("pdp__thumb--active"));
      if (idx < 0) idx = 0;
      // The SSR main image is product.featured_image, which is not always
      // images[0] - align the starting index/highlight/counter to it.
      const startBase = (mainImg.getAttribute("src") || "").split("?")[0];
      const startMatch = thumbs.findIndex((t) => (t.dataset.full || "").split("?")[0] === startBase);
      if (startMatch >= 0) {
        idx = startMatch;
        thumbs.forEach((x, n) => x.classList.toggle("pdp__thumb--active", n === idx));
        if (countEl) countEl.textContent = (idx + 1) + " / " + thumbs.length;
      }

      const preload = (i) => {
        const t = thumbs[i];
        if (!t || t._preloaded) return;
        t._preloaded = true;
        const im = new Image();
        if (t.dataset.srcset) im.srcset = t.dataset.srcset;
        im.src = t.dataset.full;
      };

      const show = (i, opts) => {
        i = (i + thumbs.length) % thumbs.length;
        idx = i;
        const t = thumbs[i];
        // srcset overrides src for display, so set both (+ sizes) or the image
        // won't visibly change. The smaller srcset widths let the browser pick a
        // right-sized file for the slot instead of always pulling full-res.
        mainImg.srcset = t.dataset.srcset || "";
        mainImg.sizes = "(max-width: 860px) 100vw, 50vw";
        mainImg.src = t.dataset.full;
        thumbs.forEach((x, n) => x.classList.toggle("pdp__thumb--active", n === i));
        if (countEl) countEl.textContent = (i + 1) + " / " + thumbs.length;
        if (!opts || opts.scroll !== false) t.scrollIntoView({ block: "nearest", inline: "nearest" });
        // Preload neighbours so the next arrow / thumb click is instant.
        preload((i + 1) % thumbs.length);
        preload((i - 1 + thumbs.length) % thumbs.length);
        if (modalOpen && zoomImg) zoomImg.src = t.dataset.full;
      };

      thumbs.forEach((t, i) => {
        on(t, "click", () => show(i));
        on(t, "mouseenter", () => preload(i));
      });
      on($('[data-gallery-prev]'), "click", (e) => { e.stopPropagation(); show(idx - 1); });
      on($('[data-gallery-next]'), "click", (e) => { e.stopPropagation(); show(idx + 1); });

      // Thumbnails now sit in a horizontal strip below the main image and
      // flex-shrink to fit, so no vertical height cap is needed. Clear any
      // stale inline max-height (e.g. left over from a cached older layout).
      if (thumbsWrap) thumbsWrap.style.maxHeight = "";

      // Zoom modal.
      const openZoom = () => {
        if (!zoomModal || !zoomImg) return;
        zoomImg.src = thumbs[idx].dataset.full;
        zoomModal.classList.add("is-open");
        zoomModal.setAttribute("aria-hidden", "false");
        document.body.classList.add("pdp-modal-open");
        modalOpen = true;
      };
      const closeZoom = () => {
        if (!zoomModal) return;
        zoomModal.classList.remove("is-open");
        zoomModal.setAttribute("aria-hidden", "true");
        document.body.classList.remove("pdp-modal-open");
        modalOpen = false;
      };
      on(mainImg, "click", openZoom);
      if (zoomModal) {
        on(zoomModal, "click", (e) => {
          if (e.target.closest("[data-image-modal-close]")) return closeZoom();
          if (e.target.closest("[data-image-modal-prev]")) { e.stopPropagation(); return show(idx - 1); }
          if (e.target.closest("[data-image-modal-next]")) { e.stopPropagation(); return show(idx + 1); }
          if (e.target === zoomModal) closeZoom();
        });
      }

      // Keyboard: Esc closes the modal; Left/Right navigate (when the modal is
      // open, or the gallery is on screen) - but never while typing in a field.
      on(document, "keydown", (e) => {
        if (e.key === "Escape" && modalOpen) return closeZoom();
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        const tag = (document.activeElement && document.activeElement.tagName) || "";
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
        if (!modalOpen && stage) {
          const r = stage.getBoundingClientRect();
          if (r.bottom < 0 || r.top > (window.innerHeight || 0)) return;
        }
        e.preventDefault();
        show(e.key === "ArrowLeft" ? idx - 1 : idx + 1);
      });

      // Let the variant picker sync the gallery when it swaps to a variant image.
      window.__pdpGallerySelectSrc = (src) => {
        if (!src) return false;
        const base = src.split("?")[0];
        const n = thumbs.findIndex((t) => (t.dataset.full || "").split("?")[0] === base);
        if (n >= 0) { show(n); return true; }
        return false;
      };
    })();
  }

  /* ============================================================
     HERO SLIDESHOW (auto-advance + manual)
     ============================================================ */
  function initHero() {
    const heroes = $$('[data-hero]');
    heroes.forEach((hero) => {
      // Guard against double-init: init() re-runs on every shopify:section:load
      // (theme editor / preview). Without this a second setInterval is started
      // on the same hero, so it advances two slides at a time.
      if (hero.dataset.heroInit) return;
      hero.dataset.heroInit = "1";
      const slides = $$('.hero__slide', hero);
      const dots = $$('.hero__dot', hero);
      const interval = parseInt(hero.dataset.heroInterval || 7000, 10);
      let idx = 0;
      let timer = null;

      const goTo = (i) => {
        idx = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle("hero__slide--active", n === idx));
        dots.forEach((d, n) => d.classList.toggle("hero__dot--active", n === idx));
      };

      const start = () => { if (slides.length > 1) timer = setInterval(() => goTo(idx + 1), interval); };
      const stop = () => clearInterval(timer);

      dots.forEach((d, i) => on(d, "click", () => { goTo(i); stop(); start(); }));
      $('[data-hero-prev]', hero) && on($('[data-hero-prev]', hero), "click", () => { goTo(idx - 1); stop(); start(); });
      $('[data-hero-next]', hero) && on($('[data-hero-next]', hero), "click", () => { goTo(idx + 1); stop(); start(); });

      on(hero, "mouseenter", stop);
      on(hero, "mouseleave", start);
      start();
    });
  }

  /* ============================================================
     COLLECTION FILTERS (sidebar)
     ============================================================ */
  function initCollectionFilters() {
    const drawerOpen = $$('[data-filters-open]');
    const drawerClose = $$('[data-filters-close]');
    const drawer = $('[data-filters-drawer]');
    drawerOpen.forEach((b) => on(b, "click", () => drawer?.classList.add("coll-filters--open")));
    drawerClose.forEach((b) => on(b, "click", () => drawer?.classList.remove("coll-filters--open")));

    $$('[data-collection-view]').forEach((b) => {
      on(b, "click", () => {
        const view = b.dataset.collectionView;
        $$('[data-collection-view]').forEach((v) => v.classList.toggle("active", v.dataset.collectionView === view));
        const grid = $('[data-collection-grid]');
        if (grid) grid.dataset.view = view;
      });
    });

    // ----- Async collection filtering (no full page reload) -----
    const results = $('[data-coll-results]');
    if (!results) return;
    let busy = false;

    // "In Stock Only" toggle: drive its href + active state from the URL on the
    // client so it is correct regardless of Liquid scope quirks, and re-sync it
    // after each AJAX swap. Toggling preserves the current sort/filters/path and
    // resets pagination.
    function syncInstock() {
      const on = new URLSearchParams(location.search).get("filter.v.availability") === "1";
      $$('[data-instock-toggle]').forEach((a) => {
        const u = new URLSearchParams(location.search);
        u.delete("page");
        if (on) u.delete("filter.v.availability"); else u.set("filter.v.availability", "1");
        const qs = u.toString();
        a.setAttribute("href", location.pathname + (qs ? "?" + qs : ""));
        a.classList.toggle("instock-toggle--on", on);
        a.setAttribute("aria-checked", on ? "true" : "false");
      });
    }
    syncInstock();

    async function navigate(url, push) {
      if (busy) return;
      busy = true;
      results.setAttribute("aria-busy", "true");
      results.style.opacity = "0.45";
      results.style.pointerEvents = "none";
      try {
        const res = await fetch(url, { headers: { "X-Requested-With": "fetch" } });
        if (!res.ok) throw 0;
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        const next = doc.querySelector("[data-coll-results]");
        if (!next) { window.location.href = url; return; }
        results.innerHTML = next.innerHTML;
        if (push !== false) history.pushState({ coll: 1 }, "", url);
        syncInstock();
        const t = doc.querySelector("title");
        if (t) document.title = t.textContent;
        const y = results.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: y < 0 ? 0 : y, behavior: "smooth" });
      } catch (err) {
        window.location.href = url;
      } finally {
        results.style.opacity = "";
        results.style.pointerEvents = "";
        results.removeAttribute("aria-busy");
        busy = false;
      }
    }

    // Build a filter URL from the form, preserving the active sort.
    function formUrl(form) {
      const usp = new URLSearchParams(new FormData(form));
      const sort = new URLSearchParams(window.location.search).get("sort_by");
      if (sort) usp.set("sort_by", sort);
      const base = (form.getAttribute("action") || window.location.pathname).split("?")[0];
      const qs = usp.toString();
      return qs ? base + "?" + qs : base;
    }

    on(results, "change", (e) => {
      const form = e.target.closest("#coll-filter-form");
      if (form && e.target.matches('input[type="checkbox"], input[type="number"]')) {
        navigate(formUrl(form));
      } else if (e.target.matches("[data-coll-sort]")) {
        const picked = new URL(e.target.value, location.origin).searchParams.get("sort_by");
        const usp = new URLSearchParams(window.location.search);
        if (picked) usp.set("sort_by", picked); else usp.delete("sort_by");
        navigate(window.location.pathname + (usp.toString() ? "?" + usp.toString() : ""));
      }
    });

    on(results, "submit", (e) => {
      const form = e.target.closest("#coll-filter-form");
      if (form) { e.preventDefault(); navigate(formUrl(form)); }
    });

    on(results, "click", (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      const a = e.target.closest("a");
      if (!a) return;
      if (a.matches(".filter-check--link, .filter-tag, .btn--link, .instock-toggle") || a.closest(".pagination")) {
        const href = a.getAttribute("href");
        if (href && href.charAt(0) === "/") { e.preventDefault(); navigate(href); }
      }
    });

    // Live "search filters" box (delegated so it survives async swaps).
    on(results, "input", (e) => {
      if (!e.target.matches("[data-filter-search]")) return;
      const q = e.target.value.trim().toLowerCase();
      const aside = e.target.closest(".coll-filters");
      if (!aside) return;
      aside.querySelectorAll(".filter-check, .filter-check--link").forEach((row) => {
        row.style.display = (!q || row.textContent.toLowerCase().indexOf(q) > -1) ? "" : "none";
      });
      aside.querySelectorAll(".filter-group").forEach((g) => {
        if (g.querySelector('input[type="number"]')) return;
        const vis = Array.prototype.some.call(
          g.querySelectorAll(".filter-check, .filter-check--link"),
          (r) => r.style.display !== "none"
        );
        g.style.display = vis ? "" : "none";
      });
    });

    if (!window.__rocoCollPop) {
      window.__rocoCollPop = true;
      window.addEventListener("popstate", () => {
        if (document.querySelector("[data-coll-results]")) navigate(window.location.href, false);
      });
    }
  }

  /* ============================================================
     ACCORDIONS (filter groups, footer on mobile, FAQ, PDP tabs)
     ============================================================ */
  function initAccordions() {
    $$('[data-accordion-trigger]').forEach((trigger) => {
      on(trigger, "click", () => {
        const target = trigger.nextElementSibling;
        if (!target) return;
        const open = target.hidden;
        target.hidden = !open;
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
  }

  /* ============================================================
     SIMPLE FORM CAPTCHA (math question + honeypot)
     ============================================================ */
  function initFormCaptcha() {
    $$('[data-form-captcha]').forEach((box) => {
      const a = 2 + Math.floor(Math.random() * 7); // 2..8
      const b = 1 + Math.floor(Math.random() * 8); // 1..9
      const q = box.querySelector('[data-captcha-q]');
      const answer = box.querySelector('[data-captcha-answer]');
      const hp = box.querySelector('[data-captcha-hp]');
      const err = box.querySelector('[data-captcha-err]');
      if (q) q.textContent = 'what is ' + a + ' + ' + b + '?';
      const form = box.closest('form');
      if (!form) return;
      on(form, 'submit', (e) => {
        const botFilled = hp && hp.value.trim() !== '';
        const correct = answer && parseInt(answer.value, 10) === a + b;
        if (botFilled || !correct) {
          e.preventDefault();
          if (err) err.hidden = false;
          if (answer) answer.focus();
        }
      });
    });
  }

  /* ----------- bootstrap ----------- */
  /* ============================================================
     PDP FITMENT WIDGET (client-side, off the saved vehicle)
     ============================================================ */
  /* The product page fitment box reads the shopper's saved vehicle
     (localStorage roco_vehicle, set by the YMM bar) and checks it against the
     product's fitment tags - so it shows a real Fits / May-not-fit verdict
     instead of linking off to a collection. The action button opens the YMM
     bar in place. Reuses handleize() / vehTag() / vehLabel() from the YMM block. */
  function initPdpFitment() {
    const box = $("[data-pdp-fitment]");
    if (!box) return;

    const tagSet = new Set(
      (box.getAttribute("data-fitment-tags") || "").split(/\s+/).filter(Boolean).map(handleize)
    );
    const titleEl = $("[data-fitment-title]", box);
    const textEl = $("[data-fitment-text]", box);
    const actionEl = $("[data-fitment-action]", box);

    const savedVehicle = () => {
      try { return JSON.parse(localStorage.getItem("roco_vehicle") || "null"); } catch { return null; }
    };

    const render = (v) => {
      box.classList.remove("pdp__fitment--fits", "pdp__fitment--no", "pdp__fitment--neutral");
      if (v && v.make && v.model && v.year) {
        const label = vehLabel(v);
        const fits = tagSet.has(vehTag(v));
        if (fits) {
          box.classList.add("pdp__fitment--fits");
          if (titleEl) titleEl.textContent = "Fits your vehicle";
          if (textEl) textEl.textContent = "This part is confirmed to fit your " + label + ".";
        } else {
          box.classList.add("pdp__fitment--no");
          if (titleEl) titleEl.textContent = "May not fit";
          if (textEl) textEl.textContent = "This part isn't listed to fit your " + label + ". Double-check before ordering.";
        }
        if (actionEl) actionEl.textContent = "Change vehicle";
      } else {
        box.classList.add("pdp__fitment--neutral");
        if (titleEl) titleEl.textContent = "Will this fit your vehicle?";
        if (textEl) textEl.textContent = "Select your vehicle to check fitment.";
        if (actionEl) actionEl.textContent = "Select Vehicle";
      }
    };

    render(savedVehicle());
    document.addEventListener("ymm:change", (e) => render(e.detail || savedVehicle()));
  }

  /* Open the global YMM bar in place (used by the PDP fitment button) instead
     of navigating away. Scrolls the sticky bar into view and focuses Make. */
  function initOpenYmm() {
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-open-ymm]");
      if (!trigger) return;
      const ymm = $("[data-ymm]");
      if (!ymm) return;
      e.preventDefault();
      // Show the picker row (not the "selected" summary) so Make/Model/Year are visible.
      ymm.dataset.ymmState = "";
      ymm.scrollIntoView({ behavior: "smooth", block: "center" });
      const mk = $("[data-ymm-make]", ymm);
      if (mk) setTimeout(() => { try { mk.focus({ preventScroll: true }); } catch { mk.focus(); } }, 450);
    });
  }

  /* ============================================================
     SHIPPING ESTIMATOR (PDP) - domestic ZIP-zone + intl zone model.
     Ported from the Roco4x4 design prototype. Works in cents; oversized
     freight items short-circuit to a quote-by-destination message.
     ============================================================ */
  function initShippingEstimator() {
    const box = $("[data-ship-est]");
    if (!box) return;

    const SHIP_GROUPS = ["United States", "North America", "Latin America & Caribbean", "Europe", "Middle East & Africa", "Asia Pacific", "Other"];
    const COUNTRIES = [
      { code: "US", name: "United States", zone: 1, group: "United States" },
      { code: "PR", name: "Puerto Rico", zone: 1, group: "United States" },
      { code: "CA", name: "Canada", zone: 2, group: "North America" },
      { code: "MX", name: "Mexico", zone: 2, group: "North America" },
      { code: "BR", name: "Brazil", zone: 3, group: "Latin America & Caribbean" },
      { code: "AR", name: "Argentina", zone: 3, group: "Latin America & Caribbean" },
      { code: "CL", name: "Chile", zone: 3, group: "Latin America & Caribbean" },
      { code: "CO", name: "Colombia", zone: 3, group: "Latin America & Caribbean" },
      { code: "CR", name: "Costa Rica", zone: 3, group: "Latin America & Caribbean" },
      { code: "PA", name: "Panama", zone: 3, group: "Latin America & Caribbean" },
      { code: "DO", name: "Dominican Republic", zone: 3, group: "Latin America & Caribbean" },
      { code: "GB", name: "United Kingdom", zone: 4, group: "Europe" },
      { code: "IE", name: "Ireland", zone: 4, group: "Europe" },
      { code: "DE", name: "Germany", zone: 4, group: "Europe" },
      { code: "FR", name: "France", zone: 4, group: "Europe" },
      { code: "ES", name: "Spain", zone: 4, group: "Europe" },
      { code: "IT", name: "Italy", zone: 4, group: "Europe" },
      { code: "NL", name: "Netherlands", zone: 4, group: "Europe" },
      { code: "SE", name: "Sweden", zone: 4, group: "Europe" },
      { code: "NO", name: "Norway", zone: 5, group: "Europe" },
      { code: "CH", name: "Switzerland", zone: 5, group: "Europe" },
      { code: "PL", name: "Poland", zone: 5, group: "Europe" },
      { code: "AE", name: "United Arab Emirates", zone: 5, group: "Middle East & Africa" },
      { code: "SA", name: "Saudi Arabia", zone: 5, group: "Middle East & Africa" },
      { code: "IL", name: "Israel", zone: 5, group: "Middle East & Africa" },
      { code: "ZA", name: "South Africa", zone: 6, group: "Middle East & Africa" },
      { code: "AU", name: "Australia", zone: 6, group: "Asia Pacific" },
      { code: "NZ", name: "New Zealand", zone: 6, group: "Asia Pacific" },
      { code: "JP", name: "Japan", zone: 5, group: "Asia Pacific" },
      { code: "SG", name: "Singapore", zone: 5, group: "Asia Pacific" },
      { code: "KR", name: "South Korea", zone: 5, group: "Asia Pacific" },
      { code: "PH", name: "Philippines", zone: 6, group: "Asia Pacific" },
      { code: "IN", name: "India", zone: 6, group: "Asia Pacific" },
      { code: "ROW", name: "Other country", zone: 6, group: "Other" },
    ];
    const US_ZONE = { 0: 1.95, 1: 1.75, 2: 1.5, 3: 1.0, 4: 1.2, 5: 1.4, 6: 1.45, 7: 1.55, 8: 1.78, 9: 2.05 };
    const findCountry = (code) => COUNTRIES.find((c) => c.code === code) || COUNTRIES[COUNTRIES.length - 1];
    const intlEta = (zone, express) => express
      ? (zone <= 2 ? "2-3 business days" : zone <= 4 ? "3-5 business days" : "4-7 business days")
      : (zone <= 2 ? "4-7 business days" : zone <= 4 ? "7-12 business days" : "10-18 business days");
    const round2 = (n) => Math.round(n * 100) / 100;
    const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));
    const dollars = (n) => fmt(Math.round(n * 100));

    const countrySel = $("[data-ship-country]", box);
    const postalInput = $("[data-ship-postal]", box);
    const goBtn = $("[data-ship-go]", box);
    const out = $("[data-ship-out]", box);
    if (!countrySel || !postalInput || !goBtn || !out) return;

    // Build the grouped country picker
    SHIP_GROUPS.forEach((g) => {
      const og = document.createElement("optgroup");
      og.label = g;
      COUNTRIES.filter((c) => c.group === g).forEach((c) => {
        const o = document.createElement("option");
        o.value = c.code;
        o.textContent = c.name;
        og.appendChild(o);
      });
      countrySel.appendChild(og);
    });
    countrySel.value = localStorage.getItem("roco_ship_country") || "US";
    postalInput.value = localStorage.getItem("roco_ship_postal") || "";

    const unitPrice = () => parseInt(box.dataset.unitPrice, 10) || 0;
    const weight = () => parseFloat(box.dataset.shipWeight) || 8;
    const isFreight = () => box.dataset.shipFreight === "1";
    const qty = () => {
      const q = $('input[name="quantity"]');
      return Math.max(1, parseInt(q && q.value, 10) || 1);
    };

    const updatePlaceholder = () => {
      const code = countrySel.value;
      postalInput.placeholder = (code === "US" || code === "PR") ? "ZIP code" : code === "CA" ? "Postal code" : "Postal code (optional)";
      postalInput.inputMode = (code === "US" || code === "PR") ? "numeric" : "text";
    };

    const validPostal = () => {
      const code = countrySel.value;
      const p = (postalInput.value || "").trim();
      if (code === "US" || code === "PR") return /^\d{5}$/.test(p);
      if (code === "CA") return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(p);
      return true; // postal optional for the rest - country + zone drives the estimate
    };

    const computeRates = () => {
      const c = findCountry(countrySel.value);
      const subtotal = unitPrice() * qty(); // cents
      const wt = weight();
      if (isFreight()) return { freight: true, intl: c.zone > 1 };
      if (c.zone === 1) {
        const zip = (postalInput.value || "").trim();
        const zone = US_ZONE[parseInt(zip[0], 10)] ?? 1.4;
        const base = Math.max(10.5, wt * 0.62 * zone); // dollars
        const ground = subtotal >= 9900 ? 0 : round2(base);
        const baseDays = Math.round(2 + zone);
        return { methods: [
          { name: "Standard Ground", carrier: "UPS Ground / USPS", price: ground, eta: baseDays + "-" + (baseDays + 2) + " business days", free: ground === 0 },
          { name: "2-Day Air", carrier: "UPS 2nd Day Air", price: round2(base * 2.35 + wt * 0.4), eta: "2 business days" },
          { name: "Overnight", carrier: "UPS Next Day Air", price: round2(base * 4.1 + wt * 0.7), eta: "1 business day" },
        ] };
      }
      const z = c.zone;
      return { intl: true, methods: [
        { name: "International Economy", carrier: "DHL eCommerce / UPS", price: round2(28 + wt * 2.1 * (z - 1) * 0.55), eta: intlEta(z, false) },
        { name: "International Express", carrier: "DHL Express / FedEx Intl", price: round2(48 + wt * 3.4 * (z - 1) * 0.6), eta: intlEta(z, true) },
      ] };
    };

    const render = (rates) => {
      if (rates.freight) {
        out.innerHTML =
          '<div class="ship-est__freight"><div class="ship-est__freight-head">Ships by ' + (rates.intl ? "international freight" : "freight") + '</div>' +
          '<p>This is an oversized item. ' +
          (rates.intl
            ? 'Cross-border freight is quoted per destination - our team confirms your exact rate and customs paperwork before we ship. Email <strong>export@roco4x4.com</strong> or call <strong>+1 (305) 392-6808</strong>.'
            : 'Freight carriers quote by destination, so we confirm your exact rate at checkout - or call <strong>(305) 392-6808</strong> for a quote now.') +
          '</p></div>';
        return;
      }
      let html = '<ul class="ship-est__rates">';
      rates.methods.forEach((m) => {
        html += '<li class="ship-est__rate"><div class="ship-est__rate-info"><div class="ship-est__rate-name">' + esc(m.name) + '</div>' +
          '<div class="ship-est__rate-meta">' + esc(m.carrier) + ' &middot; ' + esc(m.eta) + '</div></div>' +
          '<div class="ship-est__rate-price' + (m.free ? ' ship-est__rate-price--free' : '') + '">' + (m.free ? "FREE" : dollars(m.price)) + '</div></li>';
      });
      html += '</ul>';
      if (rates.intl) html += '<div class="ship-est__duties">Import duties &amp; taxes are <strong>not included</strong> - they are calculated at checkout where prepaid duties are available, or collected by the carrier on delivery.</div>';
      out.innerHTML = html;
    };

    const calculate = () => {
      if (!validPostal()) {
        const code = countrySel.value;
        out.innerHTML = '<div class="ship-est__msg ship-est__msg--err">Enter a valid ' + ((code === "US" || code === "PR") ? "5-digit ZIP code" : "postal code") + ' to see rates.</div>';
        return;
      }
      localStorage.setItem("roco_ship_country", countrySel.value);
      localStorage.setItem("roco_ship_postal", (postalInput.value || "").trim());
      out.innerHTML = '<div class="ship-est__msg">Checking carrier rates to ' + esc(findCountry(countrySel.value).name) + '&hellip;</div>';
      goBtn.disabled = true;
      setTimeout(() => { render(computeRates()); goBtn.disabled = false; }, 650);
    };

    updatePlaceholder();
    on(countrySel, "change", () => { updatePlaceholder(); out.innerHTML = ""; });
    on(postalInput, "keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); calculate(); } });
    on(goBtn, "click", calculate);
    // Re-price if the variant changes after rates were already shown
    document.addEventListener("shipest:price", () => {
      if (/ship-est__rate|ship-est__freight/.test(out.innerHTML)) calculate();
    });
  }

  function init() {
    initThemeToggle();
    initMegaMenu();
    initMobileMenu();
    Cart.init();
    YMM.init();
    SoftFitment.init();
    PredictiveSearch.init();
    initQuantitySteppers();
    initVariantPicker();
    initShippingEstimator();
    initPdpFitment();
    initOpenYmm();
    initHero();
    initCollectionFilters();
    initAccordions();
    initFormCaptcha();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init after Shopify theme-editor section updates.
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:reorder", init);

  // Expose for inline calls (footer newsletter form, etc.)
  window.Roco = { Cart, YMM, showToast, fmt };
})();
